import { useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useConfirmation } from '@/contexts/confirmation'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'

import { deleteVCard, getVCardsList } from '../services'
import { VCard } from '../types/response'
import {
    getVCardListColumns,
    getVCardActions,
} from '../utils/vcard-list-config'

export const useVCardList = () => {
    const navigate = useNavigate()
    const { setConfirmation } = useConfirmation()
    const { t } = useTranslation()

    // URL params
    const { params, queryParams, searchInputProps, setParams } =
        useUrlSearchParams({
            defaults: { sort_by: 'id', sort_order: 'desc' },
        })

    // Data fetching
    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.VCARDS, queryParams],
        queryFn: () => getVCardsList(queryParams),
        placeholderData: true,
    })

    // Delete mutation
    const { mutateAsync: doDelete, isPending: isDeleting } =
        useMutationWithHandler(
            { mutationFn: deleteVCard },
            {
                reactQueryKeys: QUERY_KEYS.VCARDS,
                successMessage: t('vcard:delete-vcard-success'),
                onSuccess: () => refetch(),
            }
        )

    // Delete handler
    const handleDelete = useCallback(
        (row: VCard) => {
            setConfirmation({
                open: true,
                title: t('vcard:confirm-delete-vcard'),
                description: t('vcard:confirm-delete-vcard-description'),
                onConfirm: async () => {
                    try {
                        await doDelete(row.id)
                    } catch (error) {
                        console.error(error)
                    }
                },
            })
        },
        [setConfirmation, doDelete, t]
    )

    // Columns & actions
    const columns = useMemo(
        () =>
            getVCardListColumns(t, (uuid) =>
                navigate(`/account/vcards/preview/${uuid}`)
            ),
        [t, navigate]
    )

    const actions = useMemo(
        () =>
            getVCardActions({
                t,
                navigate,
                onDelete: handleDelete,
                isDeleting,
            }),
        [t, navigate, handleDelete, isDeleting]
    )

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        vcards: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),

        params,
        searchInputProps,

        columns,
        actions,

        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        navigate,
        t,
    }
}
